import { useState } from 'react';
import { type BoardItem } from '@gorilla/common/src/lib/monday-api/api';
import { Box, Flex, Heading, Text, Button, Icon, Divider, Tipseen, TipseenContent, Loader } from '@vibe/core';
import { AddUpdate, Switch, Warning, MoveArrowRight, Security, Settings, MondayLogoOutline } from '@vibe/icons';
import { RegisterAsPortalManager } from './RegisterAsPortalManager';
import { ExternalLink } from './ExternalLink';

function detectEnvironment() {
  const ua = navigator.userAgent || '';
  const isElectron = ua.includes('Electron');
  const isSafari =
    ua.includes('Safari') &&
    !ua.includes('Chrome') &&
    !ua.includes('Chromium') &&
    !ua.includes('CriOS') &&
    !ua.includes('Edg') &&
    !ua.includes('OPR') &&
    !ua.includes('Electron');

  return { isElectron, isSafari };
}

type RegisterOnboardingProps = {
  isLoadingClientBoardItems: boolean;
  clientBoardItems: BoardItem[] | undefined;
  clientBoardItemsError: any;
  onContinueClick: () => void;
};

export function RegisterOnboarding({
  isLoadingClientBoardItems,
  clientBoardItems,
  clientBoardItemsError,
  onContinueClick,
}: RegisterOnboardingProps) {
  return (
    <Box
      padding="xxl"
      backgroundColor="greyBackgroundColor"
      style={{
        overflowY: 'auto',
        height: 'auto',
        minHeight: 'calc(100vh - 96px)',
      }}
    >
      <Flex
        justify="center"
        align="center"
        direction="column"
        gap={0}
        style={{
          pointerEvents: 'auto',
          userSelect: 'all',
          width: '100%',
          minHeight: 'inherit',
          opacity: 1,
          filter: 'none',
        }}
      >
        <Box
          backgroundColor="primaryBackgroundColor"
          border
          borderColor="layoutBorderColor"
          rounded="medium"
          shadow="xs"
          style={{
            width: '550px',
          }}
        >
          <Flex
            direction="column"
            gap={0}
            style={{
              width: '100%',
            }}
          >
            <Flex
              direction="column"
              gap={32}
              style={{
                padding: '56px',
                width: 'calc(100% - 112px)',
              }}
            >
              <Flex
                direction="column"
                gap={16}
                style={{
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              >
                <Flex gap={8}>
                  <Icon icon={Settings} size={18} useCurrentColor style={{ transform: 'translate3d(0,-1px,0)' }} />
                  <Text type="text2" weight="normal" align="center" color="primary" ellipsis={true} style={{ maxWidth: '420px' }}>
                    Core setup
                  </Text>
                </Flex>
                <div style={{ color: '#0073ea' }}>
                  <Heading type="h2" weight="bold" align="center" color="inherit" ellipsis={false}>
                    First let's make sure <br />
                    we're in the right board
                  </Heading>
                </div>
                <Text type="text2" align="center" color="secondary" ellipsis={false}>
                  Each item on this board should represent one of your clients. <br />
                  If the names below don't look right, the app was added to <br />
                  the wrong board. Continue only if they match clients.
                </Text>
              </Flex>
              <Box
                backgroundColor="primaryBackgroundColor"
                border
                borderColor="layoutBorderColor"
                rounded="small"
                shadow="xs"
                style={{
                  pointerEvents: 'none',
                  userSelect: 'none',
                  width: '100%',
                }}
              >
                {isLoadingClientBoardItems ? (
                  <Flex
                    justify="center"
                    align="center"
                    style={{
                      width: '100%',
                      height: '110px',
                    }}
                  >
                    <Loader size="xs" />
                  </Flex>
                ) : clientBoardItemsError ? (
                  <Flex
                    justify="center"
                    align="center"
                    style={{
                      width: '100%',
                      height: '110px',
                    }}
                  >
                    <Text type="text3" align="center" color="secondary" ellipsis={false}>
                      Error occured while fetching board items. This might be <br />
                      a temporary issue or the app doesn't have sufficient <br />
                      permissions to access the items in this board.
                    </Text>
                  </Flex>
                ) : (
                  <Flex
                    direction="column"
                    gap={0}
                    style={{
                      width: '100%',
                    }}
                  >
                    {(clientBoardItems || []).slice(0, 3).map((client, index) => (
                      <>
                        <Box
                          key={client.id}
                          style={{
                            width: 'inherit',
                          }}
                        >
                          {index > 0 ? <Divider withoutMargin /> : null}
                          <Flex
                            direction="row"
                            align="stretch"
                            justify="start"
                            gap={0}
                            style={{
                              width: '100%',
                            }}
                          >
                            <div
                              style={{
                                width: '4px',
                                backgroundColor: '#d0d4e4',
                              }}
                            />
                            <Box padding="small" paddingStart="medium" paddingEnd="medium" style={{ flex: 1 }}>
                              <Text type="text2" align="start" color="primary" style={{ maxWidth: '340px' }} ellipsis={true}>
                                {client.name}
                              </Text>
                            </Box>
                            <div>
                              <Divider direction="vertical" withoutMargin />
                            </div>
                            <Box padding="small" paddingStart="medium" paddingEnd="medium">
                              <Icon
                                icon={AddUpdate}
                                size={16}
                                useCurrentColor
                                style={{ transform: 'translate3d(0,1px,0)', color: '#676879' }}
                              />
                            </Box>
                          </Flex>
                        </Box>
                      </>
                    ))}
                    {clientBoardItems?.length === 0 ? (
                      <Flex
                        justify="center"
                        align="center"
                        style={{
                          width: '100%',
                          height: '110px',
                        }}
                      >
                        <Text type="text3" align="center" color="secondary" ellipsis={false}>
                          This board has no items, please add at least one client item.
                        </Text>
                      </Flex>
                    ) : null}
                  </Flex>
                )}
              </Box>
            </Flex>

            <Box
              backgroundColor="greyBackgroundColor"
              style={{
                width: '100%',
              }}
            >
              <Divider withoutMargin />
              <Box padding="medium" paddingStart="large">
                <Flex direction="row" gap={32}>
                  <Flex
                    direction="row"
                    gap={8}
                    style={{
                      flex: 1,
                    }}
                  >
                    <Text
                      type="text2"
                      align="start"
                      color="secondary"
                      ellipsis={false}
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                      Step 1 of 2
                    </Text>
                    <div>
                      <Icon
                        icon={MoveArrowRight}
                        size={16}
                        useCurrentColor
                        style={{ transform: 'translate3d(0,1px,0)', color: '#676879' }}
                      />
                    </div>
                    <Text type="text2" align="start" color="secondary" ellipsis={false}>
                      <ExternalLink
                        href="https://getgorilla.app/products/client-portal/how-to-use/quick-start#getstarted"
                        target="_blank"
                        style={{ color: '#676879' }}
                      >
                        Confirm your clients board
                      </ExternalLink>
                    </Text>
                  </Flex>
                  <Button
                    kind="primary"
                    size="medium"
                    disabled={isLoadingClientBoardItems || clientBoardItemsError}
                    onClick={onContinueClick}
                  >
                    Confirm
                  </Button>
                </Flex>
              </Box>
            </Box>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}

type RegisterAuthorizeProps = {
  boardId: number;
  slug?: string;
  errorMessage?: string;
};

export function RegisterAuthorize({ errorMessage, boardId, slug }: RegisterAuthorizeProps) {
  const { isElectron, isSafari } = detectEnvironment();
  const hideTipseen = !(isElectron || isSafari);
  const [isTipseenClosed, setIsTipseenClosed] = useState(hideTipseen); // Hide by default if not Electron or Safari

  return (
    <Box
      padding="xxl"
      backgroundColor="greyBackgroundColor"
      style={{
        overflowY: 'auto',
        height: 'auto',
        minHeight: 'calc(100vh - 96px)',
      }}
    >
      {!isTipseenClosed ? (
        <Tipseen
          floating
          hideCloseButton
          width={350}
          color="primary"
          content={
            <>
              <TipseenContent title="" submitButtonText="Got it" onSubmit={() => setIsTipseenClosed(true)}>
                When using the monday.com desktop application or Safari, you may experience issues with certain app features. In this case,
                we highly recommend switching browsers before proceeding.
              </TipseenContent>
            </>
          }
          containerSelector="#tipseen-floating-container"
        />
      ) : null}

      <Flex
        justify="center"
        align="center"
        direction="column"
        gap={0}
        style={{
          pointerEvents: isTipseenClosed ? 'auto' : 'none',
          userSelect: isTipseenClosed ? 'all' : 'none',
          width: '100%',
          minHeight: 'inherit',
          opacity: isTipseenClosed ? 1 : 0.25,
          filter: isTipseenClosed ? 'none' : 'grayscale(1)',
        }}
      >
        <Box
          backgroundColor="primaryBackgroundColor"
          border
          borderColor="layoutBorderColor"
          rounded="medium"
          shadow="xs"
          style={{
            width: '550px',
          }}
        >
          <Flex
            direction="column"
            gap={0}
            style={{
              width: '100%',
            }}
          >
            <Flex
              direction="column"
              gap={32}
              style={{
                padding: '56px',
                width: 'calc(100% - 112px)',
              }}
            >
              <Flex
                direction="column"
                gap={16}
                style={{
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              >
                {errorMessage ? (
                  <>
                    <Flex gap={8}>
                      <Icon icon={Warning} size={20} useCurrentColor style={{ transform: 'translate3d(0,-1px,0)' }} />
                      <Text type="text2" weight="normal" align="center" color="primary" ellipsis={true} style={{ maxWidth: '420px' }}>
                        Error
                      </Text>
                    </Flex>
                    <div style={{ color: '#0073ea' }}>
                      <Heading type="h2" weight="bold" align="center" color="inherit" ellipsis={false}>
                        Something went wrong
                      </Heading>
                    </div>
                    <Text type="text2" align="center" color="secondary" ellipsis={false}>
                      {errorMessage}
                    </Text>
                  </>
                ) : (
                  <>
                    <Flex gap={8}>
                      <Icon icon={Settings} size={18} useCurrentColor style={{ transform: 'translate3d(0,-1px,0)' }} />
                      <Text type="text2" weight="normal" align="center" color="primary" ellipsis={true} style={{ maxWidth: '420px' }}>
                        Core setup
                      </Text>
                    </Flex>
                    <div style={{ color: '#0073ea' }}>
                      <Heading type="h2" weight="bold" align="center" color="inherit" ellipsis={false}>
                        Let's connect the app to <br />
                        your monday.com user now
                      </Heading>
                    </div>
                    <Text type="text2" align="center" color="secondary" ellipsis={false}>
                      Authorize the app to fetch and modify monday.com data on your
                      <br />
                      behalf as the integration user. The integration user must be <br />
                      the board owner and remain active on this account.
                    </Text>
                  </>
                )}
              </Flex>
              {!errorMessage ? (
                <Box
                  padding="medium"
                  paddingStart="large"
                  paddingEnd="large"
                  backgroundColor="primaryBackgroundColor"
                  border
                  borderColor="layoutBorderColor"
                  rounded="small"
                  shadow="xs"
                  style={{
                    userSelect: 'none',
                    width: 'calc(100% - 48px)',
                    height: errorMessage ? 'auto' : '78px',
                  }}
                >
                  <Flex direction="column" gap={9}>
                    <Flex
                      direction="row"
                      align="center"
                      justify="center"
                      gap={8}
                      style={{
                        width: '100%',
                      }}
                    >
                      <Flex
                        gap={16}
                        direction="row"
                        align="center"
                        justify="center"
                        style={{
                          width: '100%',
                          height: '78px',
                        }}
                      >
                        <div style={{ transform: 'translate3d(-4px,1px,0)', color: 'inherit' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 20" width="35" height="30" fill="currentColor">
                            <path
                              fill="currentColor"
                              d="M19.375 0.5C21.0892 0.5 22.4999 1.86724 22.5 3.57129V16.4287C22.4999 18.1328 21.0892 19.5 19.375 19.5H3.625C1.91082 19.5 0.500077 18.1328 0.5 16.4287V3.57129C0.500077 1.86724 1.91082 0.5 3.625 0.5H19.375ZM2.8125 16.4287C2.81258 16.854 3.16545 17.2139 3.625 17.2139H7.0625V7.92871H2.8125V16.4287ZM9.375 17.2139H19.375C19.8346 17.2139 20.1874 16.854 20.1875 16.4287V7.92871H9.375V17.2139ZM2.33887 16.6885C2.34599 16.7226 2.35542 16.756 2.36523 16.7891C2.35468 16.7535 2.34533 16.7174 2.33789 16.6807L2.33887 16.6885ZM20.6455 16.7422C20.6501 16.7242 20.6573 16.7068 20.6611 16.6885L20.6621 16.6807C20.6579 16.7016 20.6508 16.7217 20.6455 16.7422ZM3.625 2.78613C3.16545 2.78613 2.81258 3.146 2.8125 3.57129V5.64258H20.1875V3.57129C20.1874 3.146 19.8346 2.78613 19.375 2.78613H3.625Z"
                            />
                          </svg>
                        </div>
                        <Icon icon={Switch} size={22} useCurrentColor style={{ transform: 'translate3d(0,1px,0)' }} />
                        <Icon icon={MondayLogoOutline} size={50} useCurrentColor style={{ transform: 'translate3d(0,1px,0)' }} />
                      </Flex>
                    </Flex>
                  </Flex>
                </Box>
              ) : null}
            </Flex>

            <Box
              backgroundColor="greyBackgroundColor"
              style={{
                width: '100%',
              }}
            >
              <Divider withoutMargin />
              <Box padding="medium" paddingStart="large">
                <Flex direction="row" gap={32}>
                  <Flex
                    direction="row"
                    gap={8}
                    style={{
                      flex: 1,
                    }}
                  >
                    {errorMessage ? (
                      <>
                        <Icon icon={Security} size={18} useCurrentColor style={{ transform: 'translate3d(0,0,0)', color: '#676879' }} />
                        <Text type="text2" align="start" color="secondary" ellipsis={false}>
                          Authorize the app to continue
                        </Text>
                      </>
                    ) : (
                      <>
                        <Text
                          type="text2"
                          align="start"
                          color="secondary"
                          ellipsis={false}
                          style={{ pointerEvents: 'none', userSelect: 'none' }}
                        >
                          Step 2 of 2
                        </Text>
                        <div>
                          <Icon
                            icon={MoveArrowRight}
                            size={16}
                            useCurrentColor
                            style={{ transform: 'translate3d(0,1px,0)', color: '#676879' }}
                          />
                        </div>
                        <Text type="text2" align="start" color="secondary" ellipsis={false}>
                          <ExternalLink
                            href="https://getgorilla.app/products/client-portal/how-to-use/quick-start#anchor-2255"
                            target="_blank"
                            style={{ color: '#676879' }}
                          >
                            Register as integration user
                          </ExternalLink>
                        </Text>
                      </>
                    )}
                  </Flex>
                  <Flex gap={16}>
                    {/*
                      <Button
                        kind="tertiary"
                        size="medium"
                        onClick={() => setCurrentStep('onboarding')}
                      >
                        Back
                      </Button>
                      */}
                    <RegisterAsPortalManager boardId={boardId} color={true} slug={slug} label="Authorize" />
                  </Flex>
                </Flex>
              </Box>
            </Box>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}

type RegisterProps = {
  clientBoardItems: BoardItem[] | undefined;
  clientBoardItemsError: any;
  isLoadingClientBoardItems: boolean;
  boardId: number;
  slug?: string;
  step?: 'onboarding' | 'registering';
};

export function Register({ clientBoardItems, clientBoardItemsError, isLoadingClientBoardItems, boardId, slug, step }: RegisterProps) {
  const [currentStep, setCurrentStep] = useState<'onboarding' | 'registering'>(step ?? 'onboarding');

  if (currentStep === 'onboarding') {
    return (
      <RegisterOnboarding
        isLoadingClientBoardItems={isLoadingClientBoardItems}
        clientBoardItems={clientBoardItems}
        clientBoardItemsError={clientBoardItemsError}
        onContinueClick={() => setCurrentStep('registering')}
      />
    );
  } else {
    return <RegisterAuthorize boardId={boardId} slug={slug} />;
  }
}
